import { useEffect, useRef, useState, type RefObject } from "react";

/**
 * Minimal iOS keyboard state tracker.
 *
 * Detects keyboard open/transitioning by watching visualViewport.resize.
 * Does NOT manipulate the viewport or layout — iOS handles positioning
 * natively (scrolls the page up to show the focused input, dvh units
 * track the visible area).
 */
export function useIOSKeyboard(
  _scrollRef: RefObject<HTMLDivElement | null>,
  _inputAreaRef: RefObject<HTMLDivElement | null>,
  _textareaRef: RefObject<HTMLTextAreaElement | null>,
): { isKeyboardOpen: boolean; isKeyboardTransitioning: boolean } {
  const [isKeyboardOpen, setIsKeyboardOpen] = useState(false);
  const [isKeyboardTransitioning, setIsKeyboardTransitioning] = useState(false);

  const baselineHeightRef = useRef(0);
  const settleTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    baselineHeightRef.current =
      window.visualViewport?.height ?? window.innerHeight;

    if (!window.visualViewport) return;

    const handler = () => {
      const vvHeight = window.visualViewport!.height;
      const baseline = baselineHeightRef.current;

      // Update baseline when we see a larger height (keyboard closed)
      if (vvHeight > baseline) {
        baselineHeightRef.current = vvHeight;
      }

      const reduction =
        (baselineHeightRef.current - vvHeight) / baselineHeightRef.current;
      const kbOpen = reduction > 0.2;

      setIsKeyboardTransitioning(true);

      if (settleTimerRef.current) {
        clearTimeout(settleTimerRef.current);
      }
      setIsKeyboardOpen(kbOpen);
      settleTimerRef.current = setTimeout(() => {
        setIsKeyboardTransitioning(false);
        settleTimerRef.current = null;
      }, 150);
    };

    window.visualViewport.addEventListener("resize", handler);

    return () => {
      if (settleTimerRef.current) {
        clearTimeout(settleTimerRef.current);
      }
      window.visualViewport?.removeEventListener("resize", handler);
    };
  }, []);

  return { isKeyboardOpen, isKeyboardTransitioning };
}
